import { useEffect, useRef, useContext } from "react"
import { doc, setDoc, serverTimestamp, getFirestore, Timestamp, writeBatch } from "firebase/firestore";
import { useLocation } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { TimeContext } from "contexts/TimeContext";
import { useCourseId, useSectionId } from "hooks/router/useUrlParams";
import { useIsMobile } from "utils/general";
import { getOrCreateSessionId } from "@cip/core/utils";
import { isStudent } from "contexts/profileUtil";
import { useQueueNewUserNudge } from "../firebase/functions/users/newUserNudgeTaskFunction";
import { useTargetCourseId } from "course/application/general/utils/apputils";
import { CourseUser } from "contexts/ProfileContext";
import { useTeachNowPresence } from "./useTeachNowPresence";



export const useEngagement = (loading: boolean, userData: CourseUser) => {
    // init interval, db, time, and location
    const intervalRef = useRef(null)
    const db = getFirestore()
    const auth = getAuth()
    const location = useLocation()
    const { getServerTimeMs, hasEditedCodeSinceUpdateRef } = useContext(TimeContext)
    const courseId = useCourseId()
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    const sectionId = useSectionId();
    const [queueNewUserNudge] = useQueueNewUserNudge();

    const isOnMobile = useIsMobile()
    const courseRole = userData?.courseRole
    const isAStudent = isStudent(courseRole)
    // The heartbeat interval outlives renders; read these through a ref so it
    // reports where the user is now, not where they were when it started.
    const hasTeachNowPresence = useTeachNowPresence({ isStudent: isAStudent, isMobile: isOnMobile })
    const latestPresenceRef = useRef({ isAStudent, isOnMobile, courseId, pathname: location.pathname, hasTeachNowPresence })
    latestPresenceRef.current = { isAStudent, isOnMobile, courseId, pathname: location.pathname, hasTeachNowPresence }

    // In the app. this is different than the actual course.
    const targetCourseId = useTargetCourseId();

    useEffect(() => {
        // If the user is not signed in, don't log
        if(loading) { return }
        const userId = auth?.currentUser?.uid;
        if(!userId) {
            return
        }

        // get the session doc
        // note: we set the session doc to the time when this hook is initialized
        const sessionDocRef = doc(db, "engagement", userId,  "sessions", Timestamp.now().toMillis().toString())
        const userDocRef = doc(db, "engagement", userId)
         
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
        const currentServerTime = getServerTimeMs()


        /**
         * Set the start time for the session. 
         */
        const setStartTime = async () => {
            // Set start time
            const batch = writeBatch(db);
            try {
                batch.set(sessionDocRef, {
                    startTime: serverTimestamp(),
                    endTime: serverTimestamp(),
                    isMobile: isOnMobile,
                }, { merge: true })
                batch.set(userDocRef, {
                    lastTimeOnSite: serverTimestamp(),
                    lastLoginTime: serverTimestamp(),
                    isMobile: isOnMobile,
                }, {merge: true})
                const isOkToEmail = userData?.okToEmail;
                const firstEmailSent = userData?.sentFirstEmail;
                if (isOkToEmail && !firstEmailSent) {
                  // Check which application path user is entering from
                  const isApplicationPathSL =
                    location.pathname.includes('/applyteach/');
                  const templateSuffix = isApplicationPathSL
                    ? '-sl'
                    : '-student';

                  await queueNewUserNudge({
                    hostCourseId: courseId,
                    courseId: targetCourseId,
                    templateSuffix,
                  });
                }
                
                batch.commit();
            } catch(e) {
                console.log("Error", e)
            } 

        }

        const resetEndTime = async () => {
            // Reset end time
            const batch = writeBatch(db);
            try {

                batch.set(sessionDocRef, {
                    endTime: serverTimestamp(),
                }, { merge: true })
                batch.set(userDocRef, {
                    lastTimeOnSite: serverTimestamp()     
                }, {merge: true})
                const latest = latestPresenceRef.current
                if(!latest.hasTeachNowPresence && latest.pathname.includes("ide/a") && hasEditedCodeSinceUpdateRef.current) {
                    batch.set(doc(db, "presence", latest.courseId, "users", userId), {
                        lastEngagedTime: hasEditedCodeSinceUpdateRef.current, // serverTimestamp(),
                        isMobile: latest.isOnMobile,
                        isStudent: latest.isAStudent
                    }, {merge: true})
                    // hasEditedCodeSinceUpdateRef.current = false
                }
                await batch.commit();
             
            // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
            } catch(e) {}
        }


        // Do once then set interval
        setStartTime()

        // intervals are in ms
        // 1000ms (1 seccond) * 60 (seconds in a minute) * 2 (minutes)
        intervalRef.current = setInterval(() => {
            resetEndTime()
        }, 1000 * 60 * 2)


        return  () => {
            // Remove timed write
            clearInterval(intervalRef.current)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    }, [loading])


    const logLocationMoment = async () => {
        const userId = auth?.currentUser?.uid
        if(!userId) {
            return
        }
        
        const presenceDocRef = doc(db, "presence", courseId, "users", userId)
        const batch = writeBatch(db);
        const locationDocRef = doc(db, "engagement", userId,  "visitedLogs", Timestamp.now().toMillis().toString())
        const assnId = location.pathname.split("ide/a")[1]
        
        // check if location contains ide/a: (do a batch write for these two writes - engagement and presence)
            // then log ideStartTime
            // and log activeAssnId
            // last_engaged_time 
        
         
        // This keeps track of how long they have been working on this assignment, where they are in the IDE, and resets last_engaged_time when they come to the IDE
        // A tab opened in the background must not reset the presence of the
        // tab the student is working in; useTeachNowPresence enters it once
        // it is shown.
        const isBackgroundTeachNowTab = latestPresenceRef.current.hasTeachNowPresence && document.visibilityState === "hidden"
        if (location.pathname.includes("ide/a") && isBackgroundTeachNowTab) {
            try {
                await setDoc(locationDocRef, {
                    timestamp: serverTimestamp(),
                    path: location.pathname,
                    isMobile: isOnMobile,
                    sessionId: getOrCreateSessionId(),
                }, {merge: true})
            } catch (e) {
                console.debug("[engagement] visit log write failed", e)
            }
        } else if (location.pathname.includes("ide/a")) {
            try {
                batch.set(presenceDocRef, {
                    lastEngagedTime: serverTimestamp(),
                    activeAssnId: assnId,
                    ideStartTime: serverTimestamp(),
                    isMobile: isOnMobile,
                    isStudent: isAStudent
                    // set to false
                }, {merge: true})
                batch.set(locationDocRef, {
                    timestamp: serverTimestamp(),
                    path: location.pathname,
                    isMobile: isOnMobile,
                    sessionId: getOrCreateSessionId(),
                }, {merge: true})
                await batch.commit()
             
            // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
            } catch(e) {}
        } else {
            try {
                await setDoc(locationDocRef, {
                    timestamp: serverTimestamp(),
                    path: location.pathname,
                    sessionId: getOrCreateSessionId(),
                }, {merge: true})
             
            // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
            } catch(e) {}
        }
    }

    // On a fresh load the IDE's presence write can run before the course role
    // has loaded, recording isStudent: false; correct it once the role arrives.
    useEffect(() => {
        const userId = auth?.currentUser?.uid
        if (!userId || !courseId || !location.pathname.includes("ide/a")) {
            return
        }
        setDoc(doc(db, "presence", courseId, "users", userId), {
            isStudent: isAStudent,
        }, { merge: true }).catch(e => {
            console.warn("[engagement] presence role update failed", e)
        })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when the role or course changes; location is covered by logLocationMoment
    }, [isAStudent, courseId])

    useEffect(() => {

        logLocationMoment()

    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    }, [location])



    return []
}


// eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
const getEmailBucket = (date) => {
    const roundDate = getRoundDate(date)
    const year = roundDate.getFullYear()
    const month = roundDate.getMonth().toString().padStart(2, "0")
    const day = roundDate.getDate().toString().padStart(2, "0")
    const hour = roundDate.getHours().toString().padStart(2, "0")

    return `${year}-${month}-${day}-${hour}`


}

const getRoundDate = (date) => {
    const minutes = date.getMinutes();
    if(minutes < 30) {
      date.setMinutes(0);
    } else {
      date.setMinutes(60);
    }
    date.setSeconds(0);
    return date;
}